import { TableHeading } from "../../../../../components/common/table/TableDesign";
import { STORAGE_PAYMENT_METHOD_OPTIONS } from "../../../invoice/invoiceUntils";

export const tableHeadings: TableHeading[] = [
  { label: "", align: "center" },
  { label: "Title", align: "left" },
  { label: "Contract Type", align: "left" },
  { label: "Start Date", align: "left" },
  { label: "End Date", align: "left" },
  { label: "Action", align: "left" },
];

export const contracts = [
  {
    title: "Contract 01",
    contractName: "No Contract",
    startDate: "",
    endDate: "",
  },
  {
    title: "Contract 02",
    contractName: "No Contract",
    startDate: "",
    endDate: "",
  },
];

// contractUtils.ts
// Mirrors the pattern used in invoiceUntils.ts — field configs + validation live
// here so ContractForm / ContractInvoicingForm stay declarative.

export const CONTRACT_STAGE_OPTIONS = [
  { id: "In storage", name: "In storage" },
  { id: "Out for delivery", name: "Out for delivery" },
  { id: "Closed", name: "Closed" },
];

export const CONTRACT_TYPE_OPTIONS = [
  { id: "storage", name: "Storage" },
  { id: "removal", name: "Removal" },
];

export const INVOICE_INTERVAL_OPTIONS = [
  { id: "Daily", name: "Daily" },
  { id: "Weekly", name: "Weekly" },
  { id: "Monthly", name: "Monthly" },
  { id: "Quarterly", name: "Quarterly" },
];

export const RATE_PER_OPTIONS = [
  { id: "Daily", name: "Daily" },
  { id: "Weekly", name: "Weekly" },
  { id: "Monthly", name: "Monthly" },
];

export const STORAGE_TYPE_OPTIONS = [
  { id: "room", name: "Room" },
  { id: "crate", name: "Crate" },
  { id: "pallet", name: "Pallet" },
  { id: "shelf", name: "Shelf" },
  { id: "container", name: "Container" },
];

// Top block — contract identity + customer + dates + notes
// Used for BOTH new + existing contract (isNewInvoice-style flag not needed here,
// title/contract_no become read-only via editReadOnly once saved).
export const CONTRACT_FORM_ELEMENTS = (
  isNewContract: boolean,
  useUnitsForInvoiceValue: boolean = false,
) => [
  {
    id: "title",
    label: "Title",
    type: "text",
    md: 3,
    editReadOnly: false,
    isRequired: true,
  },
  {
    id: "contractType",
    label: "Contract Type",
    type: "text",
    md: 3,
    // options: CONTRACT_TYPE_OPTIONS,
    isRequired: true,
  },
  {
    id: "status",
    label: "Contract Stages",
    type: "select",
    md: 3,
    options: CONTRACT_STAGE_OPTIONS,
    // stage is system-driven after creation; lock it in the UI
    editReadOnly: !isNewContract,
  },
  {
    id: "customer_name",
    label: "Customer",
    type: "text",
    md: 3,
    editReadOnly: true, // set via customer search elsewhere, not typed here
    isRequired: true,
  },
  {
    id: "invoiceAddress",
    label: "Invoice address",
    type: "text",
    md: 6,
    isRequired: true,
  },
  {
    id: "startDate",
    label: "Start Date",
    type: "date",
    md: 3,
    isRequired: true,
  },
  { id: "endDate", label: "End Date", type: "date", md: 3 },
  {
    id: "notes",
    label: "Notes",
    type: "text",
    inputProps: {
      maxLength: 250,
    },
    md: 12,
    helperText: "Only viewed by the user.",
  },
  {
    id: "invoiceInterval",
    label: "Interval",
    type: "select",
    md: 2,
    options: INVOICE_INTERVAL_OPTIONS,
    isRequired: true,
  },
  {
    id: "priceIncludesVat",
    label: "Prices include VAT",
    type: "checkbox",
    md: 2,
  },
  {
    id: "insurancePercentage",
    label: "Insurance %",
    type: "number",
    md: 2,
    patternAllowed: /^\d{0,3}(\.\d{0,2})?$/,
    isRequired: true,
  },

  {
    id: "isInsuranceAccepted",
    label: "Insurance Accepted",
    type: "checkbox",
    md: 2,
  },
  {
    id: "invoiceDescription",
    label: "Invoice Detail",
    type: "text",
    inputProps: {
      maxLength: 250,
    },
    md: 4,
  },
  {
    id: "useUnitsForInvoiceValue",
    label: "Use units for invoice value",
    type: "checkbox",
    md: 4,
  },

  {
    id: "rate",
    label: "Rate (£)",
    type: "number",
    md: 2,
    isRequired: !useUnitsForInvoiceValue,
  },
  {
    id: "ratePeriod",
    label: "Rate Period",
    type: "select",
    md: 2,
    options: RATE_PER_OPTIONS,
    isRequired: !useUnitsForInvoiceValue,
  },
  {
    id: "rateDescription",
    label: "Description",
    type: "text",
    md: 4,
    isRequired: !useUnitsForInvoiceValue,
  },
  {
    // id: "listAssignedUnitsOnInvoice",
    label: "",
    type: "title",
    md: 4,
  },
  // {
  //   id: "listAssignedUnitsOnInvoice",
  //   label: "List assigned units on invoice",
  //   type: "checkbox",
  //   md: 4,
  // },
  {
    id: "paymentMethod",
    label: "Payment Method",
    type: "select",
    md: 2,
    options: STORAGE_PAYMENT_METHOD_OPTIONS,
    isRequired: true,
  },
];

// Invoicing details block
export const CONTRACT_INVOICING_FORM_ELEMENTS = [
  {
    id: "interval",
    label: "Interval",
    type: "select",
    md: 3,
    options: INVOICE_INTERVAL_OPTIONS,
  },
  {
    id: "price_includes_vat",
    label: "Prices include VAT",
    type: "checkbox",
    md: 3,
  },
  {
    id: "insurance_percent",
    label: "Insurance %",
    type: "number",
    md: 3,
    patternAllowed: /^\d{0,3}(\.\d{0,2})?$/,
  },
  {
    id: "insurance_per",
    label: "Per",
    type: "select",
    md: 3,
    options: RATE_PER_OPTIONS,
  },
  {
    id: "invoice_detail",
    label: "Invoice detail",
    type: "text",
    md: 12,
    multiline: true,
    inputProps: { rows: 2 },
  },
  {
    id: "use_units_for_invoice_value",
    label: "Use units for invoice value",
    type: "checkbox",
    md: 4,
  },
  { id: "rate", label: "Rate £", type: "number", md: 2 },
  {
    id: "rate_per",
    label: "Per",
    type: "select",
    md: 2,
    options: RATE_PER_OPTIONS,
  },
  { id: "rate_description", label: "Description", type: "text", md: 4 },
  {
    id: "list_assigned_units_on_invoice",
    label: "List assigned units on invoice",
    type: "checkbox",
    md: 12,
  },
];

export const UNIT_TABLE_COLUMNS = [
  { id: "location", label: "Location", md: 1.5 },
  { id: "reference_no", label: "Ref.", md: 1 },
  { id: "storage_type", label: "Type", md: 1 },
  { id: "in_date", label: "In", md: 1.3 },
  { id: "out_date", label: "Out", md: 1.3 },
  { id: "provisional", label: "Provisional", md: 1 },
  { id: "invoiced_upto", label: "Invoiced upto", md: 1.3 },
  { id: "rate", label: "Rate", md: 0.8 },
  {
    id: "invoice_line_description",
    label: "Invoice line description",
    md: 2.8,
  },
];

export const emptyUnitRow = () => ({
  unit_id: null,
  location: "",
  reference_no: "",
  storage_type: "",
  in_date: null,
  in_time: null,
  out_date: null,
  out_time: null,
  provisional: false,
  invoiced_upto: null,
  rate: "",
  rate_per: "week",
  invoice_line_description: "",
});

const isEmpty = (value: any) =>
  value === null || value === undefined || String(value).trim() === "";
// -----------------------------
// Validation — same shape convention as invoiceValidateForm (returns
// { localError, isValid }) so callers can reuse existing error-state wiring.
// -----------------------------
export const contractValidateForm = (formData: any) => {
  const localError: any = {};

  if (!formData?.title?.trim()) {
    localError.title = "Title is required";
  }
  if (!formData?.contractType) {
    localError.contractType = "Contract type is required";
  }
  if (!formData?.customerName) {
    localError.customer_name = "Customer is required";
  }
  if (!formData?.invoiceAddress) {
    localError.invoiceAddress = "Invoice address is required";
  }
  if (!formData?.startDate) {
    localError.startDate = "Start date is required";
  }
  if (!formData?.invoiceInterval) {
    localError.invoiceInterval = "Invoice interval is required";
  }
  if (
    formData?.endDate &&
    formData?.startDate &&
    new Date(formData.endDate) < new Date(formData.startDate)
  ) {
    localError.endDate = "End date cannot be before start date";
  }
  if (
    formData?.insurancePercentage === null ||
    formData?.insurancePercentage === undefined ||
    formData?.insurancePercentage === ""
  ) {
    localError.insurancePercentage = "Insurance % is required";
  } else if (
    Number(formData.insurancePercentage) < 0 ||
    Number(formData.insurancePercentage) > 100
  ) {
    localError.insurancePercentage = "Insurance % must be between 0 and 100";
  }
  // Either "Use units for invoice value" is checked,
  // OR rate + ratePeriod + rateDescription must all be filled.
  if (!formData?.useUnitsForInvoiceValue) {
    if (isEmpty(formData?.rate)) {
      localError.rate = "Rate is required when not using unit values";
    } else if (Number(formData.rate) < 0) {
      localError.rate = "Rate cannot be negative";
    }

    if (isEmpty(formData?.ratePeriod)) {
      localError.ratePeriod =
        "Rate period is required when not using unit values";
    }

    if (isEmpty(formData?.rateDescription)) {
      localError.rateDescription =
        "Description is required when not using unit values";
    }
  }

  return { localError, isValid: Object.keys(localError).length === 0 };
};

export const unitRowValidate = (row: any) => {
  const errors: any = {};
  if (!row?.location) errors.location = "Required";
  if (!row?.reference_no) errors.reference_no = "Required";
  if (!row?.storage_type) errors.storage_type = "Required";
  if (!row?.in_date) errors.in_date = "Required";
  if (
    row?.out_date &&
    row?.in_date &&
    new Date(row.out_date) < new Date(row.in_date)
  ) {
    errors.out_date = "Out date cannot be before in date";
  }
  return { errors, isValid: Object.keys(errors).length === 0 };
};
